import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { Lottery } from 'src/util/types';
import config from '../../../config';
import { useAuth } from 'src/util/authenticationProvider';
import LotteryView from 'src/components/LotteryView';

export default function DefaultChannelPage() {
  const { tokens, login, refresh } = useAuth();
  const headers = useMemo(() => (tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : undefined), [tokens?.accessToken]);
  const refreshTimer = useRef<number | null>(null);

  const [lottery, setLottery] = useState<Lottery | null>(null);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(!headers);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const fetchDefaultLottery = useCallback(async () => {
    if (!headers) {
      setLottery(null);
      setGalleryImages([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [defaultRes, currentRes, galleryRes] = await Promise.all([
        axios.get(`${config.apiUrl}/api/lottery/default`, { headers }),
        axios.get(`${config.apiUrl}/api/lottery/current`, { headers }),
        axios.get(`${config.apiUrl}/api/media/all`, {
          headers,
          params: { category: 'gallery' }
        })
      ]);

      if (galleryRes?.data?.status >= 200 && Array.isArray(galleryRes.data.content)) {
        const images = galleryRes.data.content.map((media: any) => ({
          src: media.image,
          title: media.title,
          id: media.id
        }));
        setGalleryImages(images);
      } else {
        setGalleryImages([]);
      }

      const defaultLottery = defaultRes?.data?.content ?? defaultRes?.data;
      const defaultName: string = defaultLottery?.name ?? '';
      const currentPayload = currentRes?.data?.content ?? currentRes?.data;
      const currentLotteries: Lottery[] = Array.isArray(currentPayload)
        ? currentPayload
        : currentPayload
          ? [currentPayload]
          : [];

      const normalizedDefaultName = defaultName.trim().toLowerCase();
      let lotteryData: Lottery | null = null;
      if (normalizedDefaultName) {
        lotteryData =
          currentLotteries.find((lot) => lot?.name?.toLowerCase().includes(normalizedDefaultName)) ||
          null;
      } else if (currentLotteries.length > 0) {
        lotteryData = currentLotteries[0];
      }

      if (!lotteryData || lotteryData.closed) {
        setLottery(null);
        setLoading(false);
        return;
      }
      setLottery(lotteryData);
    } catch (err) {
      setError('Bitte versuchen Sie es später erneut.');
      setLottery(null);
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (headers) {
      setLoginDialogOpen(false);
      fetchDefaultLottery();
    } else {
      setLoginDialogOpen(true);
      setLoading(true);
    }
  }, [fetchDefaultLottery, headers]);

  useEffect(() => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }

    if (!tokens?.expiresAt) return;

    const delay = Math.max(tokens.expiresAt - Date.now() - 10_000, 5_000);
    refreshTimer.current = window.setTimeout(async () => {
      try {
        await refresh();
        await fetchDefaultLottery();
      } catch {
        setLoginDialogOpen(true);
      }
    }, delay);

    return () => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [fetchDefaultLottery, refresh, tokens?.expiresAt]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      await login(username.trim(), password.trim());
      setLoginDialogOpen(false);
      await fetchDefaultLottery();
    } catch (err: any) {
      setLoginError(err?.message || 'Login fehlgeschlagen');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <>
      <LotteryView lottery={lottery} galleryImages={galleryImages} loading={loading} error={error} variant="channel" />
      <Dialog open={loginDialogOpen} fullWidth maxWidth="xs">
        <form onSubmit={handleLogin}>
          <DialogTitle>Bitte anmelden</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} mt={1}>
              <TextField label="Benutzername" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
              <TextField label="Passwort" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {loginError && <Alert severity="error">{loginError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" disabled={loginLoading}>
              {loginLoading ? 'Wird angemeldet...' : 'Login'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
